import { useState } from 'react';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { Tooltip } from '../../../../components/Tooltip';
import type { EmailLengthText, GenerateEmailQuery } from '../../../../domain/generatedEmail';
import { MAX_EMAILS_TO_REGENERATE } from '../../../../domain/insight';
import { useIsOutOfEmailsQuota } from '../../../../features/insights/useIsOutOfEmailsQuota';
import { translate } from '../../../../services/i18n';
import { InsightsQuotaTooltip } from '../../InsightsQuotaTooltip';

import { RegenerateDropdown } from './RegenerateDropdown';
import { StyledFooterButton, StyledRegenerateContainer } from './styles';

type RegenerateButtonProps = {
  isStale: boolean;
  onRegenerate(length: EmailLengthText): void;
  queries: GenerateEmailQuery[];
  emailIndex: number;
};

export const RegenerateButton = (props: RegenerateButtonProps) => {
  const { isStale, queries, onRegenerate, emailIndex } = props;
  const isOutOfEmailsQuota = useIsOutOfEmailsQuota();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const isLoading = queries.some((query) => query.isLoading);
  const queriesCount = queries.length;
  const hasReachedLimit = queriesCount >= MAX_EMAILS_TO_REGENERATE;

  const getRegenerateTooltipKey = () => {
    if (hasReachedLimit) {
      return 'insights.email.regenerate_limit';
    }
    if (isStale) {
      return 'insights.email.regenerate_competitors';
    }
    return 'insights.email.regenerate_options';
  };

  const renderButton = () => {
    const isDisabled = isLoading || hasReachedLimit || isOutOfEmailsQuota;
    return (
      <StyledFooterButton
        variant="flat"
        disabled={isDisabled}
        isActive={isDropdownOpen}
        onClick={() => setIsDropdownOpen(true)}
        indicator={isStale}
        data-automation="RegenerateButton"
      >
        <SWReactIcons
          iconName="toggle-filters"
          size="xs"
          iconColor={isDisabled ? colorsPalettes.carbon[100] : colorsPalettes.carbon[300]}
        />
      </StyledFooterButton>
    );
  };

  if (isOutOfEmailsQuota) {
    return (
      <InsightsQuotaTooltip location="regenerate" minWidth={230} placement="top-right">
        {renderButton()}
      </InsightsQuotaTooltip>
    );
  }

  return (
    <StyledRegenerateContainer>
      <Tooltip
        content={translate(getRegenerateTooltipKey())}
        enabled={!isLoading ? !isDropdownOpen : null}
        minWidth={hasReachedLimit || isStale ? 180 : 146}
        placement="top-right"
        dataAutomation="RegenerateTooltip"
      >
        {renderButton()}
      </Tooltip>
      <RegenerateDropdown
        isDropdownOpen={isDropdownOpen}
        onDropdownClose={() => setIsDropdownOpen(false)}
        onRegenerate={onRegenerate}
        queries={queries}
        emailIndex={emailIndex}
      />
    </StyledRegenerateContainer>
  );
};
